import authService from './auth.service';
import { buildQuery } from '../utils/buildQuery';
import type {
  OrgConfigOut,
  OrgConfigPayload,
  OrgConfigVersionOut,
  GraphOut,
  VersionsListResponse,
  TemplateInfo,
  TemplatesListResponse,
  TemplatePayload,
  TasksByStatusResponse,
  MigrateTasksPayload,
  MigrateTasksResponse,
  BackendErrorDetail,
} from '../types/taskConfig.types';

/**
 * Error с прикреплённым `detail` из body ответа (422/409 несут structured info
 * по контракту бэкенда). Консьюмеры (AdminWorkflowBuilderPage, MigrationWizard)
 * проверяют `err.detail.code` для show-правильного модала.
 */
export class WorkflowKitError extends Error {
  detail: BackendErrorDetail;
  status: number;
  constructor(status: number, detail: BackendErrorDetail) {
    super(detail.error || detail.code || `HTTP ${status}`);
    this.name = 'WorkflowKitError';
    this.status = status;
    this.detail = detail;
  }
}

async function throwStructured(response: Response, fallbackMsg: string): Promise<never> {
  let body: unknown = null;
  try { body = await response.json(); } catch { /* empty body */ }
  const detail =
    body && typeof body === 'object' && 'detail' in body && body.detail && typeof body.detail === 'object'
      ? (body.detail as BackendErrorDetail)
      : { code: 'unknown', error: fallbackMsg };
  throw new WorkflowKitError(response.status, detail);
}

const API_URL = '/api/v1';
const ADMIN_BASE = `${API_URL}/task-manager/admin`;

/**
 * Admin endpoints для настройки workflow организации (WorkflowKit).
 * Требует permission `tasks:admin_config` или `is_admin=true`.
 *
 * Endpoints соответствуют backend handoff §3.1.
 *
 * Ошибки возвращаются как `Error(response.statusText)` — компоненты
 * должны извлечь `response.json()` сами и прогнать через
 * `workflowErrorParser.messageForError` / `fieldErrorsFromBackend`.
 * TODO: единый error-handling wrapper — смотри эталон после PR 1.
 */
class TaskConfigService {
  // ==================== Org config CRUD ====================

  async getConfig(orgId: number): Promise<OrgConfigOut> {
    const response = await authService.fetchWithAuth(`${ADMIN_BASE}/org-configs/${orgId}`);
    if (!response.ok) throw new Error(`Failed to get org config: ${response.statusText}`);
    return response.json();
  }

  /**
   * Создание/обновление полного конфига организации (batch upsert).
   *
   * @param payload — полный конфиг; при `template` — body может быть `{ organization_id }` и бэкенд применит шаблон.
   * @param opts.template — имя шаблона из `/admin/templates`; применяется вместо payload.
   */
  async saveConfig(payload: OrgConfigPayload, opts?: { template?: string }): Promise<OrgConfigVersionOut> {
    const query = buildQuery(opts ?? {});
    const response = await authService.fetchWithAuth(`${ADMIN_BASE}/org-configs${query}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.ok) await throwStructured(response, 'Failed to save org config');
    return response.json();
  }

  async getGraph(orgId: number): Promise<GraphOut> {
    const response = await authService.fetchWithAuth(`${ADMIN_BASE}/org-configs/${orgId}/graph`);
    if (!response.ok) throw new Error(`Failed to get graph: ${response.statusText}`);
    return response.json();
  }

  // ==================== Versions / rollback ====================

  async listVersions(
    orgId: number,
    params: { skip?: number; limit?: number } = {},
  ): Promise<VersionsListResponse> {
    const query = buildQuery(params);
    const response = await authService.fetchWithAuth(
      `${ADMIN_BASE}/org-configs/${orgId}/versions${query}`,
    );
    if (!response.ok) throw new Error(`Failed to list versions: ${response.statusText}`);
    return response.json();
  }

  async getVersion(orgId: number, version: number): Promise<OrgConfigVersionOut> {
    const response = await authService.fetchWithAuth(
      `${ADMIN_BASE}/org-configs/${orgId}/versions/${version}`,
    );
    if (!response.ok) throw new Error(`Failed to get version: ${response.statusText}`);
    return response.json();
  }

  async rollback(orgId: number, version: number): Promise<OrgConfigVersionOut> {
    const response = await authService.fetchWithAuth(
      `${ADMIN_BASE}/org-configs/${orgId}/rollback/${version}`,
      { method: 'POST' },
    );
    if (!response.ok) throw new Error(`Failed to rollback: ${response.statusText}`);
    return response.json();
  }

  // ==================== Templates ====================

  async listTemplates(): Promise<TemplateInfo[]> {
    const response = await authService.fetchWithAuth(`${ADMIN_BASE}/templates`);
    if (!response.ok) throw new Error(`Failed to list templates: ${response.statusText}`);
    const data: TemplatesListResponse = await response.json();
    return data.items;
  }

  async getTemplate(name: string): Promise<TemplatePayload> {
    const response = await authService.fetchWithAuth(`${ADMIN_BASE}/templates/${encodeURIComponent(name)}`);
    if (!response.ok) throw new Error(`Failed to get template: ${response.statusText}`);
    return response.json();
  }

  // ==================== Migration (Issue #1 — iterative wizard) ====================

  /**
   * Количество и preview задач в статусе — для Migration Wizard.
   * Бэкенд ограничивает preview'ом в ~100 task_ids.
   */
  async getTasksByStatus(orgId: number, statusId: number): Promise<TasksByStatusResponse> {
    const response = await authService.fetchWithAuth(
      `${ADMIN_BASE}/org-configs/${orgId}/tasks-by-status/${statusId}`,
    );
    if (!response.ok) throw new Error(`Failed to get tasks by status: ${response.statusText}`);
    return response.json();
  }

  /**
   * Bulk UPDATE задач из `from_status_id` в `to_status_id`.
   *
   * Вызывается в цикле пока `remaining > 0` — это backend-пэйджинг через `limit`.
   * Использование в MigrationWizard:
   *   while (remaining > 0) { ({ migrated, remaining } = await migrateTasks(orgId, { ... })); }
   *
   * ⚠️ Эта операция обходит transition_validator (это admin-action),
   *    но создаёт записи в task_history per task.
   */
  async migrateTasks(orgId: number, payload: MigrateTasksPayload): Promise<MigrateTasksResponse> {
    const response = await authService.fetchWithAuth(
      `${ADMIN_BASE}/org-configs/${orgId}/migrate-tasks`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) throw new Error(`Failed to migrate tasks: ${response.statusText}`);
    return response.json();
  }
}

export default new TaskConfigService();
